import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent } from '@theme/events';

const DRAWER_OFFSET = '1.6rem';

/**
 * @typedef {object} FilterDrawerRefs
 * @property {HTMLDialogElement} drawer - The filter drawer dialog element.
 * @property {HTMLFormElement} form - The filter form element.
 * @property {HTMLFormElement[]} dialogForms - Forms with method="dialog" inside the drawer.
 */

/**
 * @extends {Component<FilterDrawerRefs>}
 */
class FilterDrawer extends Component {
  /** @type {boolean} */
  isClosing = false;
  /** @type {(() => void) | null} */
  originalClose = null;
  /** @type {string | undefined} */
  sectionId;
  /** @type {HTMLElement | null} */
  backdrop = null;
  /** @type {boolean} */
  #mouseDownOnBackdrop = false;
  /** @type {symbol | null} */
  #pendingRequest = null;
  /** @type {NodeListOf<HTMLButtonElement> | null} */
  #filterButtons = null;
  /** @type {number | null} */
  #outsideClickTimeout = null;

  #getDrawerSide() {
    return this.refs.drawer?.dataset.drawerSide === 'left' ? 'left' : 'right';
  }

  #getClosedTransform() {
    return this.#getDrawerSide() === 'left'
      ? `translateX(calc(-100% - ${DRAWER_OFFSET}))`
      : `translateX(calc(100% + ${DRAWER_OFFSET}))`;
  }

  #getOpenTransform() {
    return this.#getDrawerSide() === 'left' ? `translateX(${DRAWER_OFFSET})` : `translateX(-${DRAWER_OFFSET})`;
  }

  #applyInlinePosition() {
    if (!this.refs.drawer) return;

    if (this.#getDrawerSide() === 'left') {
      this.refs.drawer.style.left = '0';
      this.refs.drawer.style.right = 'auto';
      return;
    }

    this.refs.drawer.style.right = '0';
    this.refs.drawer.style.left = 'auto';
  }

  // Event handlers bound to this instance
  /** @type {(event: Event) => void} */
  _closeHandler = this.#handleClose.bind(this);
  /** @type {(event: Event) => void} */
  _cancelHandler = this.#handleCancel.bind(this);
  /** @type {(event: Event) => void} */
  _sectionSelectHandler = this.#handleSectionSelect.bind(this);
  /** @type {(event: Event) => void} */
  _sectionDeselectHandler = this.#handleSectionDeselect.bind(this);
  /** @type {(event: MouseEvent) => void} */
  _documentClickHandler = this.#handleDocumentClick.bind(this);

  /** @type {(event: Event) => void} */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.type === 'checkbox' || target.tagName === 'SELECT') {
      this.#submitForm(this.refs.form);
    }
  };

  /** @type {(event: Event) => void} */
  #onFormSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    this.#submitForm(form);
  };

  #onPopState = () => {
    this.#renderFromURL(new URL(window.location.href), { updateHistory: false });
  };

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.drawer || !(this.refs.drawer instanceof HTMLDialogElement)) {
      console.error('FilterDrawer: drawer element not found or not a dialog element');
      return;
    }

    // Get section ID from data attribute
    this.sectionId = this.dataset.sectionId;

    // Get custom backdrop element
    const drawerId = this.refs.drawer.id;
    this.backdrop = document.querySelector(`[data-drawer-backdrop="${drawerId}"]`);

    this.#findFilterButtons();
    this.#setupEventListeners();
    this.#registerFormEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.refs.drawer) {
      this.refs.drawer.removeEventListener('close', this._closeHandler);
      this.refs.drawer.removeEventListener('cancel', this._cancelHandler);
    }

    // Clean up theme editor event listeners
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.removeEventListener('shopify:section:select', this._sectionSelectHandler);
      document.removeEventListener('shopify:section:deselect', this._sectionDeselectHandler);
    }

    this.#disableOutsideClickClose();

    this.#unregisterFormEvents();
  }

  #setupEventListeners() {
    if (!this.refs.drawer) return;

    // Save original close method first
    this.originalClose = this.refs.drawer.close.bind(this.refs.drawer);

    // Listen to Shopify theme editor events
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this._sectionSelectHandler);
      document.addEventListener('shopify:section:deselect', this._sectionDeselectHandler);
    }

    // Listen to native dialog events
    this.refs.drawer.addEventListener('close', this._closeHandler);
    this.refs.drawer.addEventListener('cancel', this._cancelHandler);
    // Close dialog on backdrop click
    // Check that both mousedown AND click are on the backdrop to avoid
    // closing when a drag starts in the content and ends on the backdrop
    this.refs.drawer.addEventListener('mousedown', (event) => {
      this.#mouseDownOnBackdrop = event.target === this.refs.drawer;
    });

    this.refs.drawer.addEventListener('click', (event) => {
      if (!this.refs.drawer || !this.originalClose) return;
      if (event.target === this.refs.drawer && this.#mouseDownOnBackdrop) {
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      }
      this.#mouseDownOnBackdrop = false;
    });

    // Close dialog on custom backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => {
        if (!this.refs.drawer || !this.originalClose) return;
        if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
          this.#animateClose(() => {
            if (this.originalClose) {
              this.originalClose();
            }
          });
        }
      });
    }

    // Intercept form submissions that close the dialog
    if (Array.isArray(this.refs.dialogForms)) {
      for (const form of this.refs.dialogForms) {
        form.addEventListener('submit', (event) => {
          if (this.refs.drawer && this.refs.drawer.hasAttribute('open') && this.originalClose) {
            event.preventDefault();
            this.#animateClose(() => {
              if (this.originalClose) {
                this.originalClose();
              }
            });
          }
        });
      }
    }

    this.#applyInlinePosition();
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Observe open attribute changes to trigger animations
    const observer = new MutationObserver(() => {
      if (!this.refs.drawer) return;

      if (this.refs.drawer.hasAttribute('open')) {
        // Opening
        this.isClosing = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#handleOpen();
          });
        });
      } else if (!this.isClosing) {
        // Closing (only if not already closing)
        this.#handleClose();
      }
    });

    observer.observe(this.refs.drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });

    // Intercept showModal to trigger opening animation
    const originalShowModal = this.refs.drawer.showModal.bind(this.refs.drawer);
    const originalShow = this.refs.drawer.show.bind(this.refs.drawer);

    this.refs.drawer.showModal = () => {
      if (!this.refs.drawer) return;
      this.#applyInlinePosition();
      this.refs.drawer.style.transform = this.#getClosedTransform();
      originalShowModal();
      // Animation will be triggered by MutationObserver
    };

    this.refs.drawer.show = () => {
      if (!this.refs.drawer) return;
      this.#applyInlinePosition();
      this.refs.drawer.style.transform = this.#getClosedTransform();
      originalShow();
      // Animation will be triggered by MutationObserver
    };

    // Intercept close to animate before closing
    this.refs.drawer.close = () => {
      if (!this.refs.drawer || !this.originalClose) return;
      // If drawer is open, animate before closing
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          if (this.originalClose) {
            this.originalClose();
          }
        });
      } else {
        this.originalClose();
      }
    };
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick(event) {
    if (!this.refs.drawer || !this.originalClose) return;
    if (!this.refs.drawer.hasAttribute('open') || this.isClosing) return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.refs.drawer.contains(target)) return;
    if (this.backdrop?.contains(target)) return;

    const { clientX, clientY } = event;
    const rect = this.refs.drawer.getBoundingClientRect();
    const isInsideDrawer = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;

    if (isInsideDrawer) return;

    this.#animateClose(() => {
      if (this.originalClose) {
        this.originalClose();
      }
    });
  }

  #enableOutsideClickClose() {
    this.#disableOutsideClickClose();

    this.#outsideClickTimeout = window.setTimeout(() => {
      document.addEventListener('click', this._documentClickHandler, true);
      this.#outsideClickTimeout = null;
    }, 0);
  }

  #disableOutsideClickClose() {
    if (this.#outsideClickTimeout !== null) {
      window.clearTimeout(this.#outsideClickTimeout);
      this.#outsideClickTimeout = null;
    }

    document.removeEventListener('click', this._documentClickHandler, true);
  }

  #registerFormEvents() {
    this.refs.form?.addEventListener('change', this.#onFormChange);
    this.refs.form?.addEventListener('submit', this.#onFormSubmit);
    window.addEventListener('popstate', this.#onPopState);
  }

  #unregisterFormEvents() {
    this.refs.form?.removeEventListener('change', this.#onFormChange);
    this.refs.form?.removeEventListener('submit', this.#onFormSubmit);
    window.removeEventListener('popstate', this.#onPopState);
  }

  #handleOpen() {
    if (!this.refs.drawer) return;

    // Show custom backdrop
    if (this.backdrop) {
      this.backdrop.classList.add('active');
    }

    this.#enableOutsideClickClose();

    // Add header--drawer-open class to header
    const header = document.querySelector('header-component');
    if (header) {
      header.classList.add('header--drawer-open');
    }

    this.#applyInlinePosition();
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Force a reflow so browser applies initial style
    this.refs.drawer.offsetHeight;

    // Use double requestAnimationFrame to ensure browser
    // has applied initial style before triggering animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.refs.drawer) return;
        // Change transform directly to trigger transition
        this.refs.drawer.style.transform = this.#getOpenTransform();
        this.refs.drawer.classList.add('filter-drawer--opening');
        this.refs.drawer.classList.add('drawer-shell--opening');
      });
    });

    this.#updateFilterButtons(true);
  }

  #handleClose() {
    // This method is called after closing to clean up
    if (!this.refs.drawer) return;
    this.refs.drawer.classList.remove('filter-drawer--opening');
    this.refs.drawer.classList.remove('drawer-shell--opening');
    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Hide custom backdrop
    if (this.backdrop) {
      this.backdrop.classList.remove('active');
    }

    this.#disableOutsideClickClose();

    // Remove header--drawer-open class from header
    const header = document.querySelector('header-component');
    if (header) {
      header.classList.remove('header--drawer-open');
    }

    this.#updateFilterButtons(false);
  }

  /**
   * @param {Event} event
   */
  #handleCancel(event) {
    event.preventDefault();
    if (!this.refs.drawer || !this.originalClose) return;
    if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
      this.#animateClose(() => {
        if (this.originalClose) {
          this.originalClose();
        }
      });
    }
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    if (!this.refs.drawer) return;

    // Mark that we're closing to avoid multiple calls
    this.isClosing = true;

    // Remove opening class
    this.refs.drawer.classList.remove('filter-drawer--opening');
    this.refs.drawer.classList.remove('drawer-shell--opening');

    this.refs.drawer.style.transform = this.#getClosedTransform();

    // Wait for animation to complete before closing
    setTimeout(() => {
      callback();
      this.isClosing = false;
    }, 300); // CSS transition duration (0.3s)
  }

  /**
   * @param {Event} event
   */
  #handleSectionSelect(event) {
    if (!this.refs.drawer) return;

    // Check if event concerns this section
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      // Open drawer when section is selected in theme editor
      if (!this.refs.drawer.hasAttribute('open')) {
        this.refs.drawer.show();
      }
    }
  }

  /**
   * @param {Event} event
   */
  #handleSectionDeselect(event) {
    if (!this.refs.drawer || !this.originalClose) return;

    // Check if event concerns this section
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    if (eventSectionId && this.sectionId && eventSectionId === this.sectionId) {
      // Close drawer when section is deselected in theme editor
      if (this.refs.drawer.hasAttribute('open') && !this.isClosing) {
        this.#animateClose(() => {
          if (this.originalClose) {
            this.originalClose();
          }
        });
      }
    }
  }

  #findFilterButtons() {
    // Find all buttons that control this drawer
    const drawerId = this.id;
    if (!drawerId) return;
    this.#filterButtons = document.querySelectorAll(`[aria-controls="${drawerId}"]`);
  }

  /**
   * @param {boolean} isOpen
   */
  #updateFilterButtons(isOpen) {
    if (!this.#filterButtons) return;

    for (const button of this.#filterButtons) {
      button.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    }
  }

  // Public methods for external access

  openDrawer() {
    if (!this.refs.drawer) return;
    this.refs.drawer.show();
  }

  closeDrawer() {
    if (!this.refs.drawer) return;
    this.refs.drawer.close();
  }

  /**
   * @param {{ href?: string, reopen?: boolean }} data
   * @param {Event} [event]
   */
  handleNavigate(data, event) {
    event?.preventDefault();
    if (!data || !data.href) return;
    const reopen = typeof data.reopen === 'boolean' ? data.reopen : Boolean(this.refs.drawer?.hasAttribute('open'));
    this.#renderFromURL(new URL(data.href, window.location.origin), { reopen });
  }

  /**
   * @param {Event} [event]
   */
  applyAndClose(event) {
    event?.preventDefault();
    if (!this.refs.form) return;
    this.#submitForm(this.refs.form, { reopen: false });
  }

  /** @param {Event} event */
  handleSortChange(event) {
    const form = this.refs.form;
    if (!(event.target instanceof HTMLSelectElement) || !form) return;
    const select = event.target;

    /** @type {HTMLSelectElement | null} */
    const existing = form.querySelector('select[name="sort_by"]');
    if (existing && existing !== select) {
      existing.value = select.value;
    }

    this.#submitForm(form);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {{ reopen?: boolean }} [options]
   */
  async #submitForm(form, options = {}) {
    if (!(form instanceof HTMLFormElement)) return;
    // Keep params the form does not own (sort_by when the drawer has no sort
    // select, q, tags, ...). The form is the source of truth only for the fields
    // it actually renders, so clear just those — an unchecked box has to drop its
    // param, but a sort chosen in the toolbar must survive.
    const formData = new FormData(form);
    const formKeys = new Set(formData.keys());
    const ownedByForm = (key) =>
      formKeys.has(key) || (key.startsWith('filter.') && form.querySelector(`[name="${CSS.escape(key)}"]`) != null);

    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    for (const key of [...params.keys()]) {
      if (ownedByForm(key)) params.delete(key);
    }

    for (const [key, value] of formData.entries()) {
      if (key === 'page') continue;
      if (typeof value !== 'string') continue;
      if (value.trim() === '') continue;
      params.append(key, value);
    }

    if (options.reopen === false && this.refs.drawer?.hasAttribute('open')) {
      await this.#waitForClose();
    }

    const url = new URL(window.location.href);
    url.search = params.toString();
    await this.#renderFromURL(url, { reopen: options.reopen });
  }

  /**
   * @param {URL} url
   * @param {{ updateHistory?: boolean, reopen?: boolean }} [options]
   */
  async #renderFromURL(url, { updateHistory = true, reopen } = {}) {
    if (!this.sectionId) return;

    const reopenDrawer = typeof reopen === 'boolean' ? reopen : Boolean(this.refs.drawer?.open);
    this.#setLoading(true);
    const requestToken = Symbol('filter-drawer-request');
    this.#pendingRequest = requestToken;

    try {
      await sectionRenderer.renderSection(this.sectionId, { url });
      if (this.#pendingRequest === requestToken) {
        const cleanUrl = new URL(url);
        cleanUrl.searchParams.delete('section_id');
        if (updateHistory) {
          window.history.pushState({}, '', cleanUrl);
        }
        const cleanSearchParams = new URLSearchParams(cleanUrl.search);
        document.dispatchEvent(new FilterUpdateEvent(cleanSearchParams));
        if (reopenDrawer) {
          requestAnimationFrame(() => {
            if (this.refs.drawer && !this.refs.drawer.open) {
              this.refs.drawer.show();
            }
          });
          if (this.backdrop) {
            this.backdrop.classList.add('active');
          } else {
            this.refs.drawer.parentElement?.querySelector('.drawer-shell__backdrop')?.classList.add('active');
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (this.#pendingRequest === requestToken) {
        this.#pendingRequest = null;
      }
      this.#setLoading(false);
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const section = document.getElementById(`shopify-section-${this.sectionId}`);
    const isBusy = Boolean(isLoading);
    if (section) {
      section.toggleAttribute('data-loading', isBusy);
    }
    this.toggleAttribute('data-loading', isBusy);
  }

  async #waitForClose() {
    if (!this.refs.drawer?.hasAttribute('open')) return;
    /** @type {Promise<void>} */
    const promise = new Promise((resolve) => {
      const dialog = this.refs.drawer;
      const handle = () => {
        dialog.removeEventListener('close', handle);
        resolve();
      };
      dialog.addEventListener('close', handle, { once: true });
      this.closeDrawer();
    });
    await promise;
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
